import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import useSalesforce from "./hooks/useSalesforce.js";
import { passwordResetRequest } from "./authConfig.js";

const APP_URL_MAP = {
  tg: import.meta.env.VITE_TG_APP_BASE_URL,
  crush: import.meta.env.VITE_DRTC_APP_BASE_URL,
  obesity: import.meta.env.VITE_OBESID_APP_BASE_URL,
  aidh: import.meta.env.VITE_AIDH_APP_BASE_URL,
};

const TARGET_APP_ALIASES = {
  tg: "tg",
  drtc: "crush",
  crush: "crush",
  obesid: "obesity",
  obesity: "obesity",
};

const normalizeTargetApp = (value) =>
  value ? TARGET_APP_ALIASES[value.toLowerCase()] || null : null;

const buildAppUrlWithTarget = (baseUrl, targetApp) => {
  if (!baseUrl || !targetApp) return null;
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set("targetApp", targetApp);
  return url.toString();
};

const persistIncomingReferrer = (url) => {
  const incomingReferrer = url.searchParams.get("r");

  if (!incomingReferrer) {
    return false;
  }

  localStorage.setItem("bootstrapReferrer", incomingReferrer);
  url.searchParams.delete("r");
  return true;
};

export function BootstrapGate({ children }) {
  const { instance } = useMsal();
  const { isSalesforceEnabled } = useSalesforce();
  const hasHandledRedirect = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (hasHandledRedirect.current) return;
    hasHandledRedirect.current = true;

    (async () => {
      try {
        console.log(
          "[BootstrapGate] useEffect triggered with isSalesforceEnabled:",
          isSalesforceEnabled,
        );
        // Accept cross-app target from URL query.
        const url = new URL(window.location.href);
        const isPasswordReset = url.searchParams.get("passwordReset") === "1";
        const currentApp = import.meta.env.VITE_BRAND || "tg";
        const queryTargetApp = normalizeTargetApp(url.searchParams.get("targetApp"));
        const hasIncomingReferrer = persistIncomingReferrer(url);
        if (queryTargetApp) {
          sessionStorage.setItem("targetApp", queryTargetApp);
          url.searchParams.delete("targetApp");
        } else {
          const isBareRootUrl = url.pathname === "/" && url.search === "" && url.hash === "";

          if (!isBareRootUrl) {
            sessionStorage.setItem("targetApp", currentApp);
          }
        }
        if (queryTargetApp || hasIncomingReferrer) {
          window.history.replaceState({}, "", url.toString());
        }

        // Always handle the promise first
        const response = await instance.handleRedirectPromise();
        if (isPasswordReset) {
          await instance.loginRedirect(passwordResetRequest);
          return;
        }

        // Check if we just came back from a login redirect with state
        if (response?.state) {
          let statePayload = null;
          try {
            statePayload = JSON.parse(response.state);
          } catch {
            statePayload = null;
          }
          const targetApp = normalizeTargetApp(statePayload?.targetApp);

          if (targetApp) {
            sessionStorage.setItem("targetApp", targetApp);

            // If the app we just logged into isn't THIS app, redirect away
            if (targetApp !== currentApp) {
              const targetUrl = buildAppUrlWithTarget(APP_URL_MAP[targetApp], targetApp);
              if (targetUrl) {
                window.location.replace(targetUrl);
                return;
              }
            }
          }
        }
      } catch (e) {
        console.error("[BootstrapGate] MSAL redirect failed", e);
      } finally {
        //   If we didn't redirect away, show the app
        setReady(true);
        sessionStorage.setItem("msalBootstrapComplete", "true");
      }
    })();
  }, [instance, isSalesforceEnabled]);

  if (!ready) {
    // You can return a loader here to verify it's working
    return <div className="loader"></div>;
  }

  return children;
}
